import { InteractionHandler } from "../Interactions";
import { DeleteModelMessage, LocalTransformFacet, UpdateFacetOnModelMessage, PrimitiveShape, PrimitiveMeshMaterial, PrimitiveMeshFacet, ReferencePlaneMetaData, MetaDataFacet, convertToNumber4, ToggleReferencePlaneAction, } from "flavr-common";
import { Quaternion } from "flavr-math";
import { modelPool, useExternalMessages } from "@modules/model";
const REFERENCE_PLANE_ORIENTATIONS = [
    new Quaternion(0, 0, 0, 1),
    new Quaternion(-Math.SQRT1_2, 0, 0, Math.SQRT1_2),
    new Quaternion(0, 0, 0, 1)
];
const REFERENCE_PLANE_PRIMITIVE_MESH = new PrimitiveMeshFacet(PrimitiveShape.Plane, 0xffffff, PrimitiveMeshMaterial.TRANSPARENT_GRID);
export class ReferencePlaneInteractionHandler extends InteractionHandler {
    constructor(metaDataMgr, locationHref) {
        super("ReferencePlane", [], [ToggleReferencePlaneAction.typestatic], []);
        this.metaDataMgr = metaDataMgr;
        this.isOperator = locationHref.includes("operator");
    }
    ToggleReferencePlaneAction(params) {
        if (ToggleReferencePlaneAction.is(params)) {
            return "Toggle Reference Plane";
        }
        return "";
    }
    performAction(params, context) {
        if (!ToggleReferencePlaneAction.is(params) ||
            context.targetUid === undefined ||
            context.position === undefined ||
            (params.operatorOnly && !this.isOperator)) {
            return;
        }
        const model = modelPool.getModel(context.targetUid);
        if (model === undefined)
            return;
        const room = model.getRoom();
        if (room === undefined)
            return;
        const referencePlanesForRoom = this.getReferencePlanesInRoom(room.getUID());
        if (referencePlanesForRoom) {
            const closestReferencePlane = this.getClosestReferencePlaneWithinMinDistance(params.orientation, context.position, params.minPlaneDistance, referencePlanesForRoom);
            if (closestReferencePlane) {
                this.deleteReferencePlane(closestReferencePlane);
                return;
            }
        }
        this.createReferencePlane(params.orientation, context.position, params.size, room.getUID(), referencePlanesForRoom);
    }
    getReferencePlanesInRoom(roomUid) {
        const metaDataForModels = this.metaDataMgr.getMetaDataOfType(ReferencePlaneMetaData);
        if (!metaDataForModels)
            return undefined;
        const referencePlanesInRoom = new Map();
        for (const [uid, metaData] of metaDataForModels) {
            const parentRoom = modelPool.getModelOrThrow(uid).getParentOrThrow();
            if (parentRoom.getUID() === roomUid) {
                referencePlanesInRoom.set(uid, metaData.data);
            }
        }
        return referencePlanesInRoom;
    }
    getClosestReferencePlaneWithinMinDistance(axis, point, minPlaneDistance, referencePlanes) {
        let closestReferencePlane;
        let closestReferencePlaneDistance = minPlaneDistance;
        for (const [uid, metaData] of referencePlanes) {
            if (metaData.axis !== axis)
                continue;
            const distance = Math.abs(metaData.position - point[axis]);
            if (distance < closestReferencePlaneDistance) {
                closestReferencePlane = uid;
                closestReferencePlaneDistance = distance;
            }
        }
        return closestReferencePlane;
    }
    deleteReferencePlane(uid) {
        this.connection.send(new DeleteModelMessage(uid));
    }
    createReferencePlane(axis, point, size, roomUid, referencePlanesForRoom) {
        const parent = modelPool.getModelOrThrow(roomUid);
        const messages = [];
        const props = useExternalMessages(messages);
        const index = this.getUnusedIndex(referencePlanesForRoom);
        const model = modelPool.createModel(`ReferencePlane${index}`, parent, props);
        const transform = new LocalTransformFacet(point, convertToNumber4(REFERENCE_PLANE_ORIENTATIONS[axis]), [size, size, size]);
        const metaData = new MetaDataFacet(new ReferencePlaneMetaData(index, axis, point[axis]));
        messages.push(new UpdateFacetOnModelMessage(model.getUID(), transform), new UpdateFacetOnModelMessage(model.getUID(), metaData), new UpdateFacetOnModelMessage(model.getUID(), REFERENCE_PLANE_PRIMITIVE_MESH));
        this.connection.sendMultiple(messages);
    }
    getUnusedIndex(referencePlanes) {
        if (!referencePlanes) {
            return 0;
        }
        let index = 0;
        for (const metaData of referencePlanes.values()) {
            if (metaData.index >= index) {
                index = metaData.index + 1;
            }
        }
        return index;
    }
}
